import './styles.scss';
import { Box } from '@mui/material';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
  ColorType,
  CrosshairMode,
  LineStyle,
} from 'lightweight-charts';
import { useEffect, useRef, useState } from 'react';

import { ChartTypeEnum } from '../../../../model/chart_type.enum';
import { getLineChartColors, lightBlue } from '../../../../theme/colors';
import { useThemeMode } from '../../../../providers/theme-provider';
import { getTokenValue } from '../../../../styles/tokens';
import Utils from '../../../../utils/immersive_utils';

interface IPoint {
  time: UTCTimestamp;
  value: number;
}
type SeriesData = IPoint[];

interface Props {
  type: ChartTypeEnum;
  data: SeriesData;
  color?: string;
  precision?: number;
  viewportResetKey?: string;
  onSeriesCreated?: (series: ISeriesApi<any>) => void;
  onSeriesDisposed?: () => void;
  onLoadMore?: (earliestTime: number) => void;
  // Opt-in hover tooltip (date + value) at the crosshair position. Off by
  // default so the other consumers of this shared chart (histogram-card,
  // last-orders-card, risk-panel-graph) are unaffected.
  showTooltip?: boolean;
  // Opt-in grid line colour override. The default `border.chartGrid` token
  // is near-invisible in dark mode (#002649 against a similarly dark chart
  // background) — the Performance card's histogram chart doesn't use this
  // shared component at all, so it was free to hardcode a more visible
  // #1f3645, which is what this override matches when a consumer wants
  // that same visible-grid look without changing it for every consumer.
  gridColor?: string;
  // Line-series stroke width. Defaults to 1 so every existing consumer
  // (histogram/last-orders/risk-panel/backtesting charts) is unaffected;
  // AccountEquityCard opts into a thicker line for its main equity series.
  lineWidth?: number;
  // Hide the "IMMERSIVE FINANCE" watermark for this instance only. Off by
  // default so every existing consumer keeps the watermark; AccountEquityCard
  // opts in for its underwater chart (Katia wants it gone from that one only).
  hideWatermark?: boolean;
  // Baseline-mode two-tone override (ignored for every other chart type).
  // The default Baseline config is one-sided (top transparent) for the
  // underwater/drawdown chart, whose data is always <= 0. A signed series that
  // crosses zero (e.g. cumulative options net-flow) needs the region ABOVE the
  // baseline coloured too. When these are provided the series fills
  // baselineTopColor above 0 and baselineBottomColor below; when omitted the
  // existing one-sided underwater behaviour is byte-for-byte unchanged.
  baselineTopColor?: string;
  baselineBottomColor?: string;
  // Additional overlaid line series on the SAME right price scale as the main
  // series — used by the PP indexed-comparison chart to plot several series at
  // once. Each carries its own explicit colour (no theme recolour, unlike the
  // palette-defaulted main series). Omitted -> no extra series, so every other
  // consumer of this shared chart is unaffected. Only meaningful for the Line
  // chart type; the comparison chart is the sole consumer.
  extraSeries?: { data: SeriesData; color: string }[];
  // Format axis/tooltip values as a percentage (append '%'). Off by default so
  // existing consumers keep plain numeric formatting. The comparison chart
  // plots cumulative % change and opts in.
  percent?: boolean;
  // Draw a prominent horizontal reference line at 0 (the rebased baseline) so
  // it's easy to read which series are above/below. Off by default; the indexed
  // comparison opts in. Only meaningful for the Line chart.
  zeroLine?: boolean;
}

interface ChartTooltipState {
  x: number;
  y: number;
  date: string;
  value: number;
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const fmtTooltipDate = (time: UTCTimestamp): string => {
  const d = new Date((time as number) * 1000);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

export const PortfolioPerformanceChartType = ({
  type,
  data,
  color,
  precision,
  viewportResetKey,
  onSeriesCreated,
  onSeriesDisposed,
  onLoadMore,
  showTooltip,
  gridColor: gridColorOverride,
  lineWidth = 1,
  hideWatermark = false,
  baselineTopColor,
  baselineBottomColor,
  extraSeries,
  percent = false,
  zeroLine = false,
}: Props) => {
  const { resolvedMode } = useThemeMode();
  const container = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi>();
  const seriesRef = useRef<ISeriesApi<any>>();
  const zeroLineRef = useRef<any>();
  const extraSeriesRef = useRef<ISeriesApi<any>[]>([]);
  const percentRef = useRef(percent);
  percentRef.current = percent;
  const lastRequestRef = useRef<number>(Infinity);
  const earliestRef = useRef<number>(Infinity);
  const isLoadingMoreRef = useRef(false);
  const lastViewportResetKeyRef = useRef<string | undefined>(undefined);
  const suppressNextVisibleRangeChangeRef = useRef(false);
  const hasBeenSizedRef = useRef(false);
  const onSeriesCreatedRef = useRef(onSeriesCreated);
  onSeriesCreatedRef.current = onSeriesCreated;
  const onSeriesDisposedRef = useRef(onSeriesDisposed);
  onSeriesDisposedRef.current = onSeriesDisposed;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const precisionRef = useRef(precision);
  precisionRef.current = precision;
  const [tooltip, setTooltip] = useState<ChartTooltipState | null>(null);

  const fitChartToContent = () => {
    const chart = chartRef.current;
    if (!chart) return;

    suppressNextVisibleRangeChangeRef.current = true;
    chart.timeScale().fitContent();
  };

  useEffect(() => {
    if (!container.current) return;

    const gridColor = gridColorOverride ?? getTokenValue('border.chartGrid', resolvedMode);
    const chart = createChart(container.current, {
      width: container.current.clientWidth,
      height: container.current.clientHeight,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: getTokenValue('text.chartAxis', resolvedMode),
      },
      grid: {
        vertLines: { color: gridColor },
        horzLines: { color: gridColor },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      timeScale: {
        borderColor: gridColor,
        timeVisible: true,
        visible: true,
        barSpacing: 20,
      },
      rightPriceScale: { autoScale: true },
      localization: {
        priceFormatter: (p: any) =>
          percent ? `${Utils.formatNumber(p, precision)}%` : Utils.formatNumber(p, precision),
      },
    });

    let s: ISeriesApi<any>;
    switch (type) {
      case ChartTypeEnum.Histogram:
        s = chart.addHistogramSeries({
          color: color || lightBlue,
          priceFormat: { type: 'percent', precision: 2, minMove: 0.1 },
        });
        break;
      case ChartTypeEnum.Area: {
        // Same addAreaSeries shape as use-create-series.ts (the other chart
        // stack's Area implementation) — topColor derived from the line
        // color at reduced opacity rather than a second prop, since this
        // component's only Area consumer (the underwater/drawdown chart)
        // just wants a single color to imply "this line, filled".
        const lineColor = color || getLineChartColors(resolvedMode)[0];
        s = chart.addAreaSeries({
          lineColor,
          topColor: `${lineColor}33`,
          bottomColor: 'transparent',
          lineWidth: 1,
          priceLineVisible: false,
        });
        break;
      }
      case ChartTypeEnum.Baseline: {
        // Unlike Area (which fills from the line down to the chart's bottom
        // axis), Baseline fills between the line and a reference value
        // (defaults to 0) — the drawdown/underwater chart's data is always
        // <= 0, so this is what actually shades the region between "how far
        // underwater" and the zero/peak reference, regardless of how deep
        // the deepest drawdown in the visible range is.
        const baseColor = color || getLineChartColors(resolvedMode)[0];
        // Two-tone opt-in: a signed series crossing zero colours above the
        // baseline as well. Omitted -> top stays transparent (underwater).
        const bottomColor = baselineBottomColor ?? baseColor;
        s = chart.addBaselineSeries({
          baseValue: { type: 'price', price: 0 },
          topLineColor: baselineTopColor ?? 'transparent',
          topFillColor1: baselineTopColor ? `${baselineTopColor}66` : 'transparent',
          topFillColor2: baselineTopColor ? `${baselineTopColor}0D` : 'transparent',
          bottomLineColor: bottomColor,
          bottomFillColor1: `${bottomColor}66`,
          bottomFillColor2: `${bottomColor}0D`,
          lineWidth: 1,
          priceLineVisible: false,
        });
        break;
      }
      default:
        s = chart.addLineSeries({
          color: color || getLineChartColors(resolvedMode)[0],
          priceScaleId: 'right',
          lineStyle: LineStyle.Solid,
          lineWidth: lineWidth as any,
          priceLineVisible: false,
        });
    }

    chartRef.current = chart;
    seriesRef.current = s;
    onSeriesCreatedRef.current?.(s);

    // Prominent zero baseline for the indexed comparison — bright white on dark,
    // a strong dark line on light, so above/below is easy to see. Stored in a ref
    // and recoloured on theme flip (the theme effect below).
    if (zeroLine) {
      zeroLineRef.current = s.createPriceLine({
        price: 0,
        color: resolvedMode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(9, 45, 58, 0.75)',
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: false,
        title: '',
      });
    }

    if (showTooltip) {
      chart.subscribeCrosshairMove((param) => {
        if (!param.point || !param.time) {
          setTooltip(null);
          return;
        }
        const point = param.seriesData.get(s) as { value?: number } | undefined;
        if (!point || typeof point.value !== 'number') {
          setTooltip(null);
          return;
        }
        setTooltip({
          x: param.point.x,
          y: param.point.y,
          date: fmtTooltipDate(param.time as UTCTimestamp),
          value: point.value,
        });
      });
    }

    // Move the TradingView attribution out of the overflow:hidden chart internals
    // so it sits in the padding strip below the x-axis.
    const outerContainer = container.current.parentElement;
    const observer = new MutationObserver(() => {
      const logo = container.current?.querySelector('a[href*="tradingview"]') as HTMLElement | null;
      if (!logo || !outerContainer) return;
      observer.disconnect();
      logo.className = 'tv-attribution';
      outerContainer.appendChild(logo);
    });
    observer.observe(container.current, { childList: true, subtree: true });

    const timeScale = chart.timeScale();
    const sub: any = timeScale.subscribeVisibleTimeRangeChange((range: any) => {
      if (suppressNextVisibleRangeChangeRef.current) {
        suppressNextVisibleRangeChangeRef.current = false;
        return;
      }
      if (!onLoadMoreRef.current || earliestRef.current === Infinity) return;
      const fromTs = Math.floor(range.from as number);
      if (isLoadingMoreRef.current) return;
      if (fromTs <= earliestRef.current && fromTs < lastRequestRef.current) {
        lastRequestRef.current = fromTs;
        isLoadingMoreRef.current = true;
        onLoadMoreRef.current(earliestRef.current);
      }
    });

    return () => {
      observer.disconnect();
      onSeriesDisposedRef.current?.();
      timeScale.unsubscribeVisibleTimeRangeChange(sub);
      chart.remove();
      chartRef.current = undefined;
      seriesRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      localization: {
        priceFormatter: (p: any) =>
          percent ? `${Utils.formatNumber(p, precision)}%` : Utils.formatNumber(p, precision),
      },
    });
  }, [precision, percent]);

  useEffect(() => {
    if (!chartRef.current) return;
    const gridColor = gridColorOverride ?? getTokenValue('border.chartGrid', resolvedMode);
    chartRef.current.applyOptions({
      layout: { textColor: getTokenValue('text.chartAxis', resolvedMode) },
      grid: { vertLines: { color: gridColor }, horzLines: { color: gridColor } },
      timeScale: { borderColor: gridColor },
    });
    if (zeroLineRef.current) {
      zeroLineRef.current.applyOptions({
        color: resolvedMode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(9, 45, 58, 0.75)',
      });
    }
    if (!color && seriesRef.current) {
      // Two-tone Baseline (net-flow cumulative): the fill/line colours are
      // baked into the series at mount, so a light/dark flip would otherwise
      // leave them on the stale hex while sibling charts recolour. Re-apply
      // the same config the mount effect uses. The `else` is the default
      // (line/underwater) path — byte-for-byte the previous behaviour.
      if (baselineTopColor || baselineBottomColor) {
        const bottomColor = baselineBottomColor ?? getLineChartColors(resolvedMode)[0];
        seriesRef.current.applyOptions({
          topLineColor: baselineTopColor ?? 'transparent',
          topFillColor1: baselineTopColor ? `${baselineTopColor}66` : 'transparent',
          topFillColor2: baselineTopColor ? `${baselineTopColor}0D` : 'transparent',
          bottomLineColor: bottomColor,
          bottomFillColor1: `${bottomColor}66`,
          bottomFillColor2: `${bottomColor}0D`,
        });
      } else {
        seriesRef.current.applyOptions({ color: getLineChartColors(resolvedMode)[0] });
      }
    }
  }, [resolvedMode, gridColorOverride, baselineTopColor, baselineBottomColor]);

  useEffect(() => {
    const s = seriesRef.current;
    const chart = chartRef.current;
    if (!s || !chart || data.length === 0) return;

    s.setData(data);
    earliestRef.current = data[0].time as number;
    isLoadingMoreRef.current = false;

    const shouldResetViewport = lastViewportResetKeyRef.current !== viewportResetKey;
    if (shouldResetViewport) {
      lastViewportResetKeyRef.current = viewportResetKey;
      lastRequestRef.current = Infinity;
      fitChartToContent();
    }
  }, [data, viewportResetKey]);

  // Overlaid comparison series. Isolated from the single-series path above:
  // when `extraSeries` is undefined (every other consumer) this effect is a
  // no-op. Line series share the main series' right price scale so all series
  // are indexed on a common axis. Rebuilt whenever the set changes (e.g. a new
  // window re-indexes every line) — cheap, and simpler than diffing.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    extraSeriesRef.current.forEach((s) => {
      try {
        chart.removeSeries(s);
      } catch {
        /* series already gone (chart disposed) */
      }
    });
    extraSeriesRef.current = [];

    if (!extraSeries || extraSeries.length === 0) return;

    extraSeries.forEach(({ data: seriesData, color: seriesColor }) => {
      const s = chart.addLineSeries({
        color: seriesColor,
        priceScaleId: 'right',
        lineStyle: LineStyle.Solid,
        lineWidth: lineWidth as any,
        priceLineVisible: false,
      });
      s.setData(seriesData);
      extraSeriesRef.current.push(s);
    });

    // Fit so the newly-added lines are in view alongside the main series.
    chart.timeScale().fitContent();
  }, [extraSeries, lineWidth]);

  useEffect(() => {
    if (!container.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        chartRef.current!.applyOptions({ width: width - 2, height });

        // Charts mounted while their tab is hidden (display: none) get fitted
        // to a zero-width container. Refit once, the first time we observe a
        // real size, so the zoom level isn't stuck at that degenerate fit.
        if (!hasBeenSizedRef.current) {
          hasBeenSizedRef.current = true;
          chartRef.current!.timeScale().fitContent();
        }
      }
    });
    ro.observe(container.current);
    return () => ro.disconnect();
  }, []);

  const isDark = resolvedMode === 'dark';
  const tooltipBg = isDark ? '#0d1f2a' : '#ffffff';
  const tooltipBorder = isDark ? '#1f3645' : 'rgba(9, 45, 58, 0.15)';
  const tooltipText = isDark ? '#e8f4fd' : '#092D3A';
  const tooltipMuted = isDark ? 'rgba(232, 244, 253, 0.6)' : 'rgba(9, 45, 58, 0.6)';

  return (
    <Box className="chart-container">
      <Box ref={container} className="chart-box" sx={{ position: 'relative' }}>
        {!hideWatermark && <div className={`watermark watermark-${resolvedMode}`} />}
        {showTooltip && tooltip && (
          <Box
            sx={{
              position: 'absolute',
              left: Math.min(tooltip.x + 12, (container.current?.clientWidth ?? 0) - 110),
              top: Math.max(tooltip.y - 44, 4),
              pointerEvents: 'none',
              zIndex: 2,
              backgroundColor: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              boxShadow: '0px 2px 6px rgba(0,0,0,0.25)',
            }}
          >
            <Box sx={{ fontSize: 9, color: tooltipMuted, lineHeight: 1.3 }}>{tooltip.date}</Box>
            <Box sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: tooltipText }}>
              {Utils.formatNumber(tooltip.value, precisionRef.current)}
              {percentRef.current ? '%' : ''}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
